/*
 * Secrets in a configuration, masked for the people who must not see them.
 *
 * A gateway's configuration carries credentials in several shapes: a fixed
 * key (`mqtt_pass`), a key inside an additional field (`mqttConnection` =
 * `{"login": …, "password": …, "server": …}`, stored as an object or as a
 * JSON string), or the user-info of a URL (`mqtt://user:pass@host`). ANEP
 * staff see them; a client's members see `••••••` wherever one sits.
 */

export const MASK = '••••••';

/** A key that names a secret: password, pass, pwd, secret, token, in any casing. */
export const isSecretKey = (key: string): boolean => /pass(word)?$|pwd|secret|token/i.test(key);

const URL_USERINFO = /^([a-z][a-z0-9+.-]*:\/\/[^/@\s:]+:)([^@\s]+)(@)/i;

const parseJsonObject = (text: string): Record<string, unknown> | unknown[] | null => {
  const trimmed = text.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

/** Whether a value holds a secret somewhere: by key, or in a URL's user-info. */
export const hasSecret = (value: unknown, key?: string): boolean => {
  if (key && isSecretKey(key)) return value !== null && value !== undefined && value !== '';
  if (typeof value === 'string') {
    if (URL_USERINFO.test(value)) return true;
    const parsed = parseJsonObject(value);
    return parsed !== null && hasSecret(parsed);
  }
  if (Array.isArray(value)) return value.some((v) => hasSecret(v));
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value as Record<string, unknown>).some(([k, v]) => hasSecret(v, k));
  }
  return false;
};

/**
 * The same value with every secret replaced by the mask: nested objects and
 * arrays are walked, a JSON string is parsed, masked and re-serialised the
 * same way (compact), and a URL loses its password. Values without a secret
 * come back as they are.
 */
export const maskSecrets = <T>(value: T, key?: string): T => {
  if (key && isSecretKey(key)) {
    return (value === null || value === undefined || value === '' ? value : MASK) as T;
  }
  if (typeof value === 'string') {
    const parsed = parseJsonObject(value);
    if (parsed !== null) {
      const masked = maskSecrets(parsed);
      return (masked === parsed ? value : JSON.stringify(masked)) as T;
    }
    return value.replace(URL_USERINFO, `$1${MASK}$3`) as T;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((v) => { const m = maskSecrets(v); if (m !== v) changed = true; return m; });
    return (changed ? out : value) as T;
  }
  if (typeof value === 'object' && value !== null) {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const m = maskSecrets(v, k);
      if (m !== v) changed = true;
      out[k] = m;
    }
    return (changed ? out : value) as T;
  }
  return value;
};
